import { useRoute } from 'vue-router';
import { computed, watch } from 'vue';
import Cookies from 'js-cookie';
import { kpwebFetch } from '~/plugins/customFetch';

export default function useTracking() {
  const route = useRoute();
  let config = {
    listenRoute: true,
  };

  const currentRoute = computed(() => route);

  const DISALLOWED_TRACKING_ENV = [];

  const trackEvent = async (name, data) => {
    // if the user is not logged in, return an empty object
    if (!Cookies.get('token')) {
      return {};
    }

    if (!window.keyprod?.env || DISALLOWED_TRACKING_ENV.includes(window.keyprod.env)) {
      return {};
    }

    const sessionRecordingProps = window.mixpanel?.get_session_recording_properties
      ? window.mixpanel.get_session_recording_properties()
      : {};

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventName: name,
        eventData: {
          user_agent: navigator.userAgent,
          user_language: navigator.language,
          user_platform: window.navigator.platform,
          user_screen: `${window.screen.width}x${window.screen.height}`,
          route_name: currentRoute.value.name ?? null,
          route_params: currentRoute.value.params ?? {},
          full_path: currentRoute.value.fullPath ?? null,
          session_replay_url: sessionRecordingProps
            ? window.mixpanel?.get_session_replay_url() : null,
          session_replay_id: sessionRecordingProps ? sessionRecordingProps.$mp_replay_id : null,
          ...data,
        },
      }),
    };
    try {
      const response = await kpwebFetch('/tracking/send', requestOptions);
      return response?.ok ? await response.json().catch(() => { }) : {};
    } catch (error) {
      console.error('Error sending tracking event:', error);
      return {};
    }
  };

  /**
     * A little helper to get the "settings_type" value under the "Settings Viewed" event
     */
  const getSettingsTypeValue = () => {
    const mapping = {
      'settings.alerts': 'Alerts',
      'settings.assignments': 'Assignments',
      'settings.closingTimes': 'Closing Times',
      'settings.dataExport': 'Data Export',
      'settings.dataExport.form': 'Data Export',
      'settings.downtimeCauses': 'Downtime Causes',
      'settings.downtimeCauses.recurringEvents': 'Recurring Events',
      'settings.executiveReport': 'Executive Report',
      'settings.machines': 'Machines',
      'settings.machinesObjectives': 'Machines Objectives',
      'settings.manufacturing_orders': 'Manufacturing Orders',
      'settings.notifications': 'Notifications',
      'settings.openingTimes': 'Opening Times',
      'settings.planning_options': 'Planning Options',
      'settings.products': 'Products',
      'settings.recurringEvents': 'Recurring Events',
      'settings.users': 'Users',
      'settings.widgetExports': 'Widget Export',
      'settings.newFeatures': 'New Features',
    };

    const value = mapping[currentRoute.value.name];
    if (value) { return value; }

    return null;
  };

  /**
     * Returns true if the current route matches the given route name
     * @param routeName
     * @returns {boolean}
     */
  const routeMatch = (routeName) => currentRoute.value
        && currentRoute.value.matched.some((routeRecord) => routeRecord.name === routeName);

  const startListenning = () => {
    // Use a primitive string as the watch source so Vue compares by value (===).
    // This ensures the callback only fires on genuine navigations (route name or
    // params change) and NOT on query-string changes (filters: equipments,
    // eventTypes, dates…). Using an object source with deep:true is unreliable
    // because currentRoute is a Vue Router Proxy — deep traversal can leak
    // reactive reads from query into the tracked dependencies.
    watch(
      () => `${currentRoute.value.name}|${JSON.stringify(currentRoute.value.params ?? {})}`,
      () => {
        if (currentRoute.value?.meta?.mixPanel) {
          trackEvent(currentRoute.value.meta.mixPanel, {});
        }

        // We want to track some pages under settings
        if (routeMatch('settings')) {
          const settingsType = getSettingsTypeValue();
          if (!settingsType) {
            return;
          }

          trackEvent(
            'Settings Viewed',
            {
              settings_type: settingsType,
            },
          );
        }
      },
      { immediate: true },
    );
  };

  const trackInit = (initConfig) => {
    config = {
      ...config,
      ...initConfig,
    };

    if (config.listenRoute) {
      startListenning();
    }
  };

  return { getSettingsTypeValue, trackEvent, trackInit };
}
